import { runtimeSession } from "@/runtime/session";
import { UserManager, WebStorageStateStore, type User, type UserManagerSettings } from "oidc-client-ts";
import { OIDC_CALLBACK_PATH, getAuthMode, safeNext, type AuthMode } from "@/lib/authMode";
import { clearSignInAttempt, markSignInAttempt, recentSignInAttempt } from "@/lib/signInGuard";

// Sign-in for the Telephony Control Plane (unified-platform spec 2026-10-15 §4.2).
// Keycloak mode: the UserManager comes from GET /api/auth/mode (client
// "asterisk", PKCE, callback /auth/callback) and the API gets access tokens.
// Legacy mode: today's Cognito settings from VITE_COGNITO_*, and ID tokens.
export const OIDC_SCOPE = "openid email profile";

export type LegacyEnv = { authority?: string; clientId?: string; domain?: string };

type Renewable = {
  getUser(): Promise<{ refresh_token?: string } | null>;
  signinSilent(): Promise<unknown>;
};

let manager: UserManager | null = null;
let renewInFlight: Promise<boolean> | null = null;

export function keycloakSettings(mode: AuthMode, origin: string, store: Storage): UserManagerSettings | null {
  if (mode.mode !== "keycloak") return null;
  return {
    authority: mode.authority, // real discovery (no hand-written metadata)
    client_id: mode.client_id,
    redirect_uri: `${origin}${OIDC_CALLBACK_PATH}`,
    post_logout_redirect_uri: origin,
    response_type: "code", // oidc-client-ts always uses PKCE (S256) for the code flow
    scope: OIDC_SCOPE,
    // Keycloak brokers Google; skip its chooser. An existing Keycloak session
    // still signs in silently (the cookie step runs first).
    extraQueryParams: { kc_idp_hint: "google" },
    // Renewal is explicit (renew()): rotated refresh tokens are single-use.
    automaticSilentRenew: false,
    monitorSession: false,
    userStore: new WebStorageStateStore({ store }),
  };
}

// Today's Cognito settings, unchanged; null (not a throw) when the build has none.
export function legacySettings(env: LegacyEnv, origin: string, store: Storage): UserManagerSettings | null {
  const authority = env.authority;
  const clientId = env.clientId;
  const domain = env.domain?.replace(/\/$/, "");
  if (!authority || !clientId || !domain) return null;
  return {
    authority,
    client_id: clientId,
    redirect_uri: origin,
    post_logout_redirect_uri: origin,
    response_type: "code",
    scope: OIDC_SCOPE,
    automaticSilentRenew: true,
    monitorSession: false,
    userStore: new WebStorageStateStore({ store }),
    metadata: {
      issuer: authority,
      authorization_endpoint: `${domain}/oauth2/authorize`,
      token_endpoint: `${domain}/oauth2/token`,
      userinfo_endpoint: `${domain}/oauth2/userInfo`,
      jwks_uri: `${authority}/.well-known/jwks.json`,
      end_session_endpoint: `${domain}/logout`,
    },
  };
}

export function initUserManager(mode: AuthMode = getAuthMode()): UserManager | null {
  const origin = window.location.origin;
  const store = window.sessionStorage;
  const settings =
    mode.mode === "keycloak"
      ? keycloakSettings(mode, origin, store)
      : legacySettings(
          {
            authority: import.meta.env.VITE_COGNITO_AUTHORITY,
            clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
            domain: import.meta.env.VITE_COGNITO_DOMAIN,
          },
          origin,
          store,
        );
  manager = settings ? new UserManager(settings) : null;
  return manager;
}

export const getUserManager = (): UserManager | null => manager;

// Keycloak mode sends the access token; legacy mode the Cognito ID token, which
// is what the unified-platform Cognito verifier requires (token_use == "id").
export function bearerFrom(user: User | null, mode: AuthMode): string | null {
  if (!user || user.expired) return null;
  return (mode.mode === "keycloak" ? user.access_token : user.id_token) || null;
}

export async function currentBearerToken(): Promise<string | null> {
  const runtime = runtimeSession();
  if (runtime) {
    try { return runtime.token(); } catch { return null; }
  }
  return manager ? bearerFrom(await manager.getUser(), getAuthMode()) : null;
}

export function renew(mgr: Renewable | null = manager): Promise<boolean> {
  const runtime = runtimeSession();
  if (runtime) return runtime.refresh().then(() => true, () => false);
  // Keycloak rotates refresh tokens (reuse 0): parallel callers share one renewal.
  if (!renewInFlight) {
    renewInFlight = (async () => {
      if (!mgr) return false;
      try {
        // Without a refresh token signinSilent would use a hidden iframe whose
        // redirect lands on /auth/callback (no silent handler) and hang.
        const user = await mgr.getUser();
        if (!user?.refresh_token) return false;
        return Boolean(await mgr.signinSilent());
      } catch {
        return false;
      }
    })().finally(() => {
      renewInFlight = null;
    });
  }
  return renewInFlight;
}

export async function login(next = "/"): Promise<void> {
  if (runtimeSession()) throw new Error("Return to the unified shell to sign in.");
  if (!manager) return;
  if (getAuthMode().mode === "keycloak") {
    await manager.signinRedirect({ state: { next: safeNext(next) } });
  } else {
    await manager.signinRedirect({ extraQueryParams: { identity_provider: "Google" } });
  }
}

// Keycloak mode, the token could not be renewed: back to Keycloak once; within
// 60 s of the last attempt show the login page's button instead (loop guard).
export async function signInAgain(next: string): Promise<void> {
  if (runtimeSession()) throw new Error("Your shell session has expired.");
  if (recentSignInAttempt()) {
    window.location.hash = "#/login";
    return;
  }
  markSignInAttempt();
  await login(next);
}

export async function completeSignIn(): Promise<string> {
  if (!manager) return "/";
  const user = await manager.signinRedirectCallback();
  return safeNext((user.state as { next?: unknown } | undefined)?.next);
}

// Keycloak: its end-session endpoint (from discovery), back to the origin.
// Legacy: Cognito /logout, as today.
export async function logout(): Promise<void> {
  if (runtimeSession()) throw new Error("Use the unified shell account menu to sign out.");
  clearSignInAttempt();
  if (manager) await manager.signoutRedirect();
}

export function __resetAuthForTests(): void {
  manager = null;
  renewInFlight = null;
}
